///
/// Copyright (c) 2017-present, Siklu Ltd.
/// All rights reserved.
///

import { Injectable } from '@angular/core';
import { GtthCustomerService } from '../../../../services/customer/customer.service';
import { GtthLocalStorageService } from '../../../auth/localStorage.service';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { iif, of, switchMap } from 'rxjs';
import { map, tap } from 'rxjs/operators';
import { getCustomersList, getSelectedCustomerInfo, noOpAction, setCustomersList, setSelectedCustomerInfo } from '../accordion.actions';
import { Store } from '@ngrx/store';
import { selectCustomersList } from '../accordion.selectors';
import { selectRouteData } from '../../../../router-store/router.selector';
import { blockCustomer, createCustomer, deleteCustomer, editCustomer } from '../../../../customers-config/store/customer-config.actions';
import { PreloaderService } from '../../../../services/preloader.service';
import { getUsersPage } from '../../../../users/store/users-list.actions';
import { concatLatestFrom } from '@ngrx/operators';
import { PARAM_CUSTOMER_ID, PARAM_TREE_ID } from '../../../constants/url-params.constants';
import { isDefined } from '@siklu/sui-lib/utils';

@Injectable()
export class AccordionEffects {
    getCustomersList$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(getCustomersList),
            tap(() => this.preloader.show()),
            switchMap((action) => this.customerService.query(action.filter, action.baseUrl)),
            map((customersList) => setCustomersList({ customersList }))
        );
    });

    setCustomersList$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(setCustomersList, deleteCustomer, blockCustomer, createCustomer, editCustomer),
            concatLatestFrom(() => this.store.select(selectCustomersList)),
            map(([, customers]) => {
                let selectedCustomerId = this.localStorageService.getSelectedCustomerId();
                const isCustomerExist = customers.some((customer) => customer.id === selectedCustomerId);
                if (!isCustomerExist) {
                    selectedCustomerId = customers[0]?.id;
                    this.localStorageService.setSelectedCustomerId(selectedCustomerId);
                }

                const queryParams = {};
                if (isDefined(selectedCustomerId)) {
                    queryParams[PARAM_CUSTOMER_ID] = selectedCustomerId;
                } else {
                    queryParams[PARAM_CUSTOMER_ID] = undefined;
                    queryParams[PARAM_TREE_ID] = undefined;
                }

                this.router.navigate([], {
                    queryParams,
                    queryParamsHandling: 'merge'
                });

                return getSelectedCustomerInfo({ selectedCustomerId });
            })
        );
    });

    getAdditionalCustomerInfo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(getSelectedCustomerInfo),
            tap(() => this.preloader.show()),
            switchMap((props) =>
                iif(() => isDefined(props.selectedCustomerId), this.customerService.find(props.selectedCustomerId), of(null))
            ),
            map((selectedCustomer) => setSelectedCustomerInfo({ customer: selectedCustomer }))
        );
    });

    setAdditionalCustomerInfo$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(setSelectedCustomerInfo),
            concatLatestFrom(() => this.store.select(selectRouteData)),
            map(([, routerData]) => {
                if (routerData.tabName === 'customers' || routerData.tabName === 'user-activity-overview') {
                    return getUsersPage();
                } else {
                    this.preloader.hide();
                    return noOpAction();
                }
            })
        );
    });

    constructor(
        private readonly store: Store,
        private readonly actions$: Actions,
        private readonly customerService: GtthCustomerService,
        private readonly localStorageService: GtthLocalStorageService,
        private readonly preloader: PreloaderService,
        private readonly router: Router
    ) {}
}
